import { createContext, useCallback, useContext, useEffect, useMemo, type PropsWithChildren } from 'react';
import {
  Button,
  Text,
  UNSTABLE_Toast as ReactAriaToast,
  UNSTABLE_ToastContent as ToastContent,
  UNSTABLE_ToastQueue as ToastQueue,
  UNSTABLE_ToastRegion as ToastRegion,
} from 'react-aria-components/Toast';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastMessage {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Sin valor, el aviso permanece hasta que se cierre manualmente. */
  timeout?: number;
}

interface ToastContentValue {
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  showToast(message: ToastMessage): string;
  closeToast(key: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const queue = new ToastQueue<ToastContentValue>({ maxVisibleToasts: 3 });

/**
 * Avisos visibles y anunciados del sitio.
 *
 * Toda dependencia de la API todavía inestable de React Aria queda encerrada
 * aquí. Las pantallas solo conocen `useToast`, y por eso no habrá que migrar
 * cada uso cuando Toast pase a ser estable.
 */
export function ToastProvider({ children }: PropsWithChildren) {
  const showToast = useCallback((message: ToastMessage) => {
    const timeout = message.timeout === undefined ? undefined : Math.max(message.timeout, 5000);
    return queue.add(
      {
        title: message.title,
        description: message.description,
        tone: message.tone ?? 'info',
      },
      timeout === undefined ? undefined : { timeout },
    );
  }, []);
  const closeToast = useCallback((key: string) => queue.close(key), []);
  const value = useMemo(() => ({ showToast, closeToast }), [closeToast, showToast]);

  // La cola es global para poder conservar identidad estable, pero no debe
  // sobrevivir al desmontaje de la aplicación ni contaminar otra prueba.
  useEffect(() => () => queue.clear(), []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion queue={queue} aria-label="Avisos" className="ta-toast-region">
        {({ toast }) => (
          <ReactAriaToast
            toast={toast}
            className={`ta-toast ta-toast--${toast.content.tone}`}
          >
            <ToastContent className="ta-toast-content">
              <Text slot="title" className="ta-toast-title">{toast.content.title}</Text>
              {toast.content.description ? (
                <Text slot="description" className="ta-toast-description">
                  {toast.content.description}
                </Text>
              ) : null}
            </ToastContent>
            <Button
              slot="close"
              className="btn-close ta-toast-close"
              aria-label={`Cerrar aviso: ${toast.content.title}`}
            />
          </ReactAriaToast>
        )}
      </ToastRegion>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast debe utilizarse dentro de ToastProvider.');
  return context;
}
